<i18n src="./resources/locales.json"></i18n>

<template>
  <EcsDashboardCard :disabled="disabled" :dark="$vuetify.theme.dark" @resize="expanded => $emit('resize', expanded)">
    <div class="title-container">
      <div v-once class="card-title">
        {{ $t('queueStatus') }}
      </div>

      <v-spacer />

      <div class="bp-selection">
        <v-select
          class="bp-select"
          dense
          :items="businessProcessesStats"
          :label="$t('businessProcess')"
          :disabled="!!onlyForBusinessProcessId"
          item-value="businessProcessId"
          item-text="businessProcessName"
          :value="selectedBusinessProcessId"
          @change="changeSelectedBP"
        />
      </div>
    </div>

    <div class="card-graph-container">
      <div ref="graph" class="graph" />

      <div class="qs-legend">
        <div class="legend-title">
          {{ totalAgents }}
          Agents
        </div>
        <div v-for="(data, i) in dataset" :id="'qs-legend-' + i" :key="i">
          <span class="bullet" :style="{ backgroundColor: data.color }"></span>
          <span class="legend-value">{{ data.value }}</span>
          <span class="legend-description">{{ data.i18n }}</span>
        </div>
      </div>
    </div>

    <div class="total-stat">
      <div class="total-stat-count">
        {{ queuedInteractions }}
      </div>
      <div v-once class="total-stat-description">
        {{ $t('queuedInteractions') }}
      </div>
    </div>

    <div class="total-stat">
      <div class="total-stat-count" :live-dot="waitingInteractions">
        <ecs-live-time-ago v-if="waitingInteractions" :since="longestWaiting" short-output verbose />
        <span v-else>0s</span>
      </div>
      <div v-once class="total-stat-description">
        {{ $t('longestWaiting') }}
      </div>
    </div>

    <div class="total-stat">
      <div class="total-stat-count">
        {{ callbacksToBeProcessed }}
      </div>
      <div v-once class="total-stat-description">
        {{ $t('callbacksToBeProcessed') }}
      </div>
    </div>

    <div class="clear"></div>

    <div class="footer-info">
      {{ $t('lastReset') }}
      {{ lastReset }}
    </div>
  </EcsDashboardCard>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import EcsDashboardCard from '@/components/ecs-dashboard-card/EcsDashboardCard';
import PieGraph from '@/components/ecs-queue-status-card/d3-pie-graph/d3-pie-graph';
import EcsKpiFormatter from '@/mixins/ecs-kpi-formatter/EcsKpiFormatter';
import BusinessProcess from '@/classes/BusinessProcess';
import EcsLiveTimeAgo from '@/components/ecs-live-time-ago/EcsLiveTimeAgo';

export default {
  name: 'EcsQueueStatusCard',

  components: {
    EcsLiveTimeAgo,
    EcsDashboardCard
  },

  mixins: [EcsKpiFormatter],

  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    onlyForBusinessProcessId: {
      type: String,
      default: ''
    },
    startBusinessProcessId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      dataset: [],
      expanded: false,
      graph: null,
      search: '',
      selectedBusinessProcessId: ''
    };
  },

  computed: {
    ...mapGetters('STATS', ['BUSINESS_PROCESSES_STATS']),

    businessProcessesStats() {
      const bps = this.BUSINESS_PROCESSES_STATS;
      if (this.onlyForBusinessProcessId) {
        const onlySelectedBp = bp => bp.businessProcessId === this.onlyForBusinessProcessId;
        const bp = bps.find(onlySelectedBp);
        return bp ? [bp] : [];
      } else {
        return bps.sort(BusinessProcess.compare);
      }
    },

    totalAgents() {
      const sum = (sum, item) => sum + item.value;
      return this.dataset.reduce(sum, 0);
    },

    queuedInteractions() {
      return this.getBpProp('businessProcessWaitingInteractions') || 0;
    },

    waitingInteractions() {
      return !!this.getBpProp('businessProcessWaitingInteractions');
    },

    longestWaiting() {
      return this.getBpProp('businessProcessLongestWaitingTime');
    },

    callbacksToBeProcessed() {
      return this.getBpProp('callbackToBeProcessed') || 0;
    },

    lastReset() {
      return moment((this.selectedBusinessProcess || {}).lastResetTime).fromNow();
    },
    selectedBusinessProcess() {
      const bp = this.businessProcessesStats.find(bp => bp.businessProcessId === this.selectedBusinessProcessId);
      if (!bp) {
        return {};
      }
      return new BusinessProcess(bp);
    }
  },

  watch: {
    BUSINESS_PROCESSES_STATS: {
      handler() {
        if (!this.graph) {
          this.initialize();
        } else {
          this.updateGraph();
        }
      },
      deep: true
    },

    selectedBusinessProcess() {
      this.updateGraph();
    }
  },

  mounted() {
    this.initialize();
    this.selectedBusinessProcessId = this.startBusinessProcessId;
  },

  methods: {
    changeSelectedBP(value) {
      this.selectedBusinessProcessId = value;
      this.$emit('saveCardStartingBusinessProcess', value);
    },

    getBpProp(key) {
      if (!this.selectedBusinessProcess) {
        return null;
      }
      return (this.selectedBusinessProcess[key] || {}).value;
    },

    getFirstBpId() {
      return this.businessProcessesStats[0].businessProcessId;
    },

    async initialize() {
      if (!this.businessProcessesStats.length) {
        return;
      }
      await this.$nextTick();
      this.graph = new PieGraph(this.$refs.graph, []);
      if (!this.selectedBusinessProcessId || this.selectedBusinessProcessId.length === 0) {
        if (this.onlyForBusinessProcessId && this.onlyForBusinessProcessId.length > 0) {
          this.changeSelectedBP(this.onlyForBusinessProcessId);
        } else {
          this.changeSelectedBP(this.getFirstBpId());
        }
      }
      this.updateGraph();
    },

    async updateGraph() {
      await this.$nextTick();

      this.dataset = [
        {
          i18n: this.$t('ready'),
          value: this.selectedBusinessProcess.totalReadyAgents,
          color: '#4ea96d'
        },
        {
          i18n: this.$t('busy'),
          value: this.selectedBusinessProcess.totalBusyAgents,
          color: '#f44336'
        },
        {
          i18n: this.$t('unavailable'),
          value: this.selectedBusinessProcess.totalUnavailableAgents,
          color: '#ffc107'
        }
      ];

      if (this.graph && this.graph.updateDataset) {
        this.graph.updateDataset(this.dataset);
      }
    }
  }
};
</script>

<style src="./scoped.css" scoped></style>
<style>
.graph path {
  transition: all 0.3s ease;
}
</style>
